import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { Helmet } from 'react-helmet';
import { FiSun } from 'react-icons/fi';
import NavBar from '../../components/layout/NavBar';
import Footer from '../../components/layout/Footer';
import { blockApi } from '../../services/api';
import SpinningSun from '../../components/common/SpinningSun';
import { getNormalizedBlockStatus } from '../../utils/statusUtils';
import './BlockMap.css';

const BlockMap = () => {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [map, setMap] = useState(null);
  const [selectedBlock, setSelectedBlock] = useState(null);
  const mapRef = useRef(null);
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);
  const [scriptLoading, setScriptLoading] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [domReady, setDomReady] = useState(false);
  
  // Track map state changes
  useEffect(() => {
    // Map state tracking for debugging in development
    if (process.env.NODE_ENV === 'development') {
      // Map state monitoring
    }
  }, [map]);

  // Track Google Maps loaded state
  useEffect(() => {
    // Google Maps loaded state tracking
  }, [googleMapsLoaded]);

  // Track DOM ready state
  useEffect(() => {
    // DOM ready state tracking
  }, [domReady]);

  // Check if DOM element is ready
  useLayoutEffect(() => {
    const checkDomReady = () => {
      const element = mapRef.current || document.getElementById('map');
      
      if (element) {
        setDomReady(true);
      } else {
        // Try again after a short delay
        setTimeout(checkDomReady, 50);
      }
    };
    
    checkDomReady();
  }, []); // Run once after layout

  const fetchBlocks = useCallback(async () => {
    setLoading(true);
    try {
      const data = await blockApi.getBlocksWithStats();
      const blocksData = data.blocks;

      // Debug: Log the raw data first
      console.log('Raw blocks data:', blocksData);

      // Process blocks and add normalized status
      const processedBlocks = blocksData.map(block => {
        // Get normalized status for consistent status calculation
        const normalizedStatus = getNormalizedBlockStatus(
          block, 
          block.activeErrors || block.errors || [], 
          block.activeWarnings || block.warnings || []
        );
        
        // console.log(`Block ${block.name}: Status=${normalizedStatus.status}, Reason=${normalizedStatus.reason}`);
        
        return {
          ...block,
          normalizedStatus: normalizedStatus.status,
          statusReason: normalizedStatus.reason
        };
      });

      // All blocks will be shown on the map
      const blocksForMap = processedBlocks.map(block => ({
        ...block,
        hasRealAddress: block.address && block.address !== 'Unknown' && block.address.trim() !== ''
      }));

      console.log('Processed blocks for map:', blocksForMap);
      
      // Debug: Show which blocks have real addresses
      blocksForMap.forEach(block => {
        console.log(`${block.name}: Address="${block.address}", HasReal=${block.hasRealAddress}, Location="${block.location}"`);
      });
      setBlocks(blocksForMap);
    } catch (err) {
      setError('Error fetching blocks: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBlocks();
  }, [fetchBlocks]);

  // Load Google Maps script
  useEffect(() => {
    // Check if API key is present
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!apiKey || apiKey === 'YOUR_API_KEY_HERE') {
      setError('Google Maps API key is missing. Please add REACT_APP_GOOGLE_MAPS_API_KEY to your .env file.');
      return;
    }

    // Add global error handler for Google Maps API errors
    const handleMapError = (error) => {
      console.error('Google Maps API Error:', error);
      if (error.includes('ApiNotActivatedMapError')) {
        setError(`Google Maps JavaScript API error. Please verify in Google Cloud Console: 
        1. "Maps JavaScript API" is enabled
        2. Billing is enabled (required even for free tier)
        3. API key has no domain restrictions OR localhost is allowed
        4. API key is not restricted to wrong APIs`);
      }
    };
    
    window.gm_authFailure = () => {
      console.error('Google Maps authentication failure');
      handleMapError('ApiNotActivatedMapError');
    };
    window.gm_authFailure.toString = () => 'gm_authFailure';

    // Load Google Maps script if not already loaded
    if (!window.google && !googleMapsLoaded && !scriptLoading) {
      setScriptLoading(true);
      
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        // Test if the API is actually accessible
        setTimeout(() => {
          if (window.google && window.google.maps) {
            setGoogleMapsLoaded(true);
          } else {
            console.error('Google Maps API loaded but not accessible - likely an API key issue');
            setError('Google Maps API key issue. Please check your API key and restrictions in Google Cloud Console.');
          }
          setScriptLoading(false);
        }, 200);
      };
      script.onerror = () => {
        console.error('Failed to load Google Maps script');
        setError('Failed to load Google Maps script. Please check your API key and ensure the "Maps JavaScript API" is enabled in your Google Cloud Console.');
        setScriptLoading(false);
      };
      document.head.appendChild(script);
    } else if (window.google?.maps) {
      setGoogleMapsLoaded(true);
    }
    
    // Cleanup function
    return () => {
      // Clean up global error handler
      if (window.gm_authFailure) {
        delete window.gm_authFailure;
      }
    };
  }, [googleMapsLoaded, scriptLoading]);

  // Initialize map when both conditions are met
  useEffect(() => {
    const initializeMap = () => {
      if (!googleMapsLoaded || !window.google?.maps) {
        return;
      }

      if (!domReady) {
        return;
      }

      if (map) {
        return;
      }

      // Get the map element directly if ref fails
      const mapElement = mapRef.current || document.getElementById('map');
      if (!mapElement) {
        // Try again after a short delay to let React finish rendering
        setTimeout(initializeMap, 100);
        return;
      }

      const mapDiv = mapElement;
      
      // Check dimensions
      if (mapDiv.offsetWidth === 0 || mapDiv.offsetHeight === 0) {
        if (retryCount < 5) {
          setRetryCount(prev => prev + 1);
          setTimeout(initializeMap, 300);
          return;
        } else {
          setError('Unable to initialize map: container has no dimensions. Please refresh the page.');
          return;
        }
      }
      
      // Reset retry count on successful dimension check
      setRetryCount(0);

      try {
        // Clear any existing content to prevent conflicts
        mapDiv.innerHTML = '';
        
        const mapInstance = new window.google.maps.Map(mapDiv, {
          zoom: 10,
          center: { lat: 37.7749, lng: -122.4194 }, // Default to San Francisco
          styles: [
            {
              "featureType": "all",
              "elementType": "geometry.fill",
              "stylers": [{"weight": "2.00"}]
            },
            {
              "featureType": "all",
              "elementType": "geometry.stroke",
              "stylers": [{"color": "#9c9c9c"}]
            },
            {
              "featureType": "all",
              "elementType": "labels.text",
              "stylers": [{"visibility": "on"}]
            },
            {
              "featureType": "landscape",
              "elementType": "all",
              "stylers": [{"color": "#f2f2f2"}]
            },
            {
              "featureType": "landscape",
              "elementType": "geometry.fill",
              "stylers": [{"color": "#ffffff"}]
            },
            {
              "featureType": "landscape.man_made",
              "elementType": "geometry.fill",
              "stylers": [{"color": "#ffffff"}]
            },
            {
              "featureType": "poi",
              "elementType": "all",
              "stylers": [{"visibility": "off"}]
            },
            {
              "featureType": "road",
              "elementType": "all",
              "stylers": [{"saturation": -100}, {"lightness": 45}]
            },
            {
              "featureType": "road",
              "elementType": "geometry.fill",
              "stylers": [{"color": "#eeeeee"}]
            },
            {
              "featureType": "road",
              "elementType": "labels.text.fill",
              "stylers": [{"color": "#7b7b7b"}]
            },
            {
              "featureType": "road",
              "elementType": "labels.text.stroke",
              "stylers": [{"color": "#ffffff"}]
            },
            {
              "featureType": "road.highway",
              "elementType": "all",
              "stylers": [{"visibility": "simplified"}]
            },
            {
              "featureType": "road.arterial",
              "elementType": "labels.icon",
              "stylers": [{"visibility": "off"}]
            },
            {
              "featureType": "transit",
              "elementType": "all",
              "stylers": [{"visibility": "off"}]
            },
            {
              "featureType": "water",
              "elementType": "all",
              "stylers": [{"color": "#46bcec"}, {"visibility": "on"}]
            },
            {
              "featureType": "water",
              "elementType": "geometry.fill",
              "stylers": [{"color": "#c8d7d4"}]
            },
            {
              "featureType": "water",
              "elementType": "labels.text.fill",
              "stylers": [{"color": "#070707"}]
            },
            {
              "featureType": "water",
              "elementType": "labels.text.stroke",
              "stylers": [{"color": "#ffffff"}]
            }
          ]
        });
        
        setMap(mapInstance);
      } catch (error) {
        console.error('Error initializing Google Maps:', error);
        
        if (error.message.includes('ApiNotActivatedMapError') || error.toString().includes('ApiNotActivatedMapError')) {
          setError('Google Maps JavaScript API is not enabled. Please enable the "Maps JavaScript API" in your Google Cloud Console at console.cloud.google.com');
        } else if (error.message.includes('InvalidKeyMapError') || error.toString().includes('InvalidKeyMapError')) {
          setError('Invalid Google Maps API key. Please check your API key in the .env file.');
        } else if (error.message.includes('RefererNotAllowedMapError') || error.toString().includes('RefererNotAllowedMapError')) {
          setError('Domain not allowed. Please add your domain to the API key restrictions in Google Cloud Console.');
        } else if (error.message.includes('QuotaExceededError') || error.toString().includes('QuotaExceededError')) {
          setError('API quota exceeded. Please check your billing and quota limits in Google Cloud Console.');
        } else {
          setError('Failed to load map: ' + error.message + ' (Type: ' + error.name + ')');
        }
      }
    };

    // Add small delay to ensure DOM is ready
    const timeoutId = setTimeout(initializeMap, 100);

    // Cleanup
    return () => clearTimeout(timeoutId);
  }, [googleMapsLoaded, domReady, map, retryCount]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (map && mapRef.current) {
        try {
          // Clear the map container to prevent DOM conflicts
          if (mapRef.current.firstChild) {
            mapRef.current.innerHTML = '';
          }
          setMap(null);
        } catch (error) {
          console.warn('Error during map cleanup:', error);
        }
      }
    };
  }, [map]);

  // Add markers when blocks data is loaded and map is ready
  useEffect(() => {
    if (map && blocks.length > 0) {
      console.log('Adding markers for blocks:', blocks);
      const geocoder = new window.google.maps.Geocoder();
      const bounds = new window.google.maps.LatLngBounds();
      let markersPlaced = 0;

      const addMarkerToMap = (block, position, isDefaultLocation = false) => {
        // All map pins are always orange
        const markerColor = '#FBB040'; // Block brand orange
        
        const marker = new window.google.maps.Marker({
          position: position,
          map: map,
          title: block.location || block.name || 'Block Sunscreen Dispenser',
                          icon: {
                  url: 'data:image/svg+xml;base64,' + btoa(`
                    <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="20" cy="20" r="18" fill="${markerColor}" stroke="#fff" stroke-width="4"/>
                      <text x="20" y="26" font-family="Arial, sans-serif" font-size="16" font-weight="bold" text-anchor="middle" fill="#fff">B</text>
                    </svg>
                  `),
                  scaledSize: new window.google.maps.Size(40, 40),
                  anchor: new window.google.maps.Point(20, 20)
                }
        });

        // Create info window content
        const getStatusDisplay = (status) => {
          const statusMap = {
            'healthy': '🟢 Healthy',
            'warning': '🟡 Warning', 
            'critical': '🔴 Critical'
          };
          return statusMap[status] || '⚪ Unknown';
        };
        
        // Determine if device is online (customer-friendly)
        const isOnline = block.normalizedStatus === 'healthy' || block.normalizedStatus === 'warning';
        const onlineStatus = isOnline ? '🟢 Online' : '⚪ Offline';
        
        const infoWindow = new window.google.maps.InfoWindow({
          content: `
            <div class="map-info-window">
              <h3>${block.name || 'Block Sunscreen Dispenser'}</h3>
              
              <p><strong>Status:</strong> ${onlineStatus}</p>
              
              ${block.hasRealAddress ? 
                `<p><strong>Address:</strong> ${block.address}</p>` : 
                `<p><strong>Location:</strong> ${block.location || 'Location not configured'}</p>`
              }
              
              ${block.image ? 
                `<div style="margin: 10px 0;"><img src="${block.image}" alt="Block device" style="max-width: 200px; height: auto; border-radius: 8px;"/></div>` : 
                ''
              }
              
              ${!isDefaultLocation ? '' : 
                `<p><em style="color: #666; font-size: 12px;">Approximate location shown</em></p>`
              }
            </div>
          `
        });

        // Add click listener to marker
        marker.addListener('click', () => {
          // Close any open info windows
          if (selectedBlock && selectedBlock.infoWindow) {
            selectedBlock.infoWindow.close();
          }
          
          infoWindow.open(map, marker);
          setSelectedBlock({ marker, infoWindow, block });
        });

        // Extend bounds to include this marker
        bounds.extend(position);
        markersPlaced++;
        
        // Fit map to show all markers after all are processed
        if (markersPlaced === blocks.length) {
          map.fitBounds(bounds);
          
          // Don't zoom in too much if there's only one marker
          const listener = window.google.maps.event.addListener(map, 'idle', () => {
            if (map.getZoom() > 16) map.setZoom(16);
            window.google.maps.event.removeListener(listener);
          });
        }
      };

      blocks.forEach((block, index) => {
        // console.log(`Processing block ${index + 1}/${blocks.length}: ${block.name}`);
        
        // Try to geocode the real address first
        if (block.hasRealAddress) {
          geocoder.geocode({ address: block.address }, (results, status) => {
            if (status === 'OK' && results[0]) {
              // console.log(`Geocoded ${block.name} successfully`);
              addMarkerToMap(block, results[0].geometry.location, false);
            } else {
              console.warn(`⚠️ Geocoding failed for ${block.name}: ${status}. Using default location.`);
              // Fall back to default location
              const defaultPosition = new window.google.maps.LatLng(37.7749, -122.4194); // San Francisco
              addMarkerToMap(block, defaultPosition, true);
            }
          });
        } else {
          // No real address, use default location
          console.log(`📍 ${block.name} has no configured address, placing at default location`);
          const defaultPosition = new window.google.maps.LatLng(37.7749 + (index * 0.01), -122.4194 + (index * 0.01)); // Slightly offset each block
          addMarkerToMap(block, defaultPosition, true);
        }
      });
    }
  }, [map, blocks, selectedBlock]);


  if (loading) {
    return (
      <div className="block-map-container">
        <NavBar />
        <SpinningSun 
          message="Loading Block locations..."
          size="extra-large"
        />
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="block-map-container">
        <NavBar />
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button onClick={fetchBlocks} className="retry-button">Retry</button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="block-map-container">
      <Helmet>
        <title>Block Locations - Block Automation</title>
        <meta property="og:title" content="Block Locations - Block Automation" />
        <meta property="og:description" content="Find Block sunscreen dispensers near you." />
      </Helmet>
      
      <NavBar />
      
      {/* Hero Section */}
      <div className="map-hero-section">
        <div className="max-width">
          <span className="beforeHeading">FIND NEARBY</span>
          <h1 className="map-title">Block Locations</h1>
          <p className="map-subtitle">
            Discover Block sunscreen dispensers in your area. Click on any pin to see details about that location.
          </p>
        </div>
      </div>
      
      {/* Map Section */}
      <div className="map-section">
        <div className="map-stats">
          <div className="stat">
            <span className="stat-number">{blocks.length}</span>
            <span className="stat-label">Total Locations</span>
          </div>
          <div className="stat">
            <span className="stat-number">{blocks.filter(b => 
              b.normalizedStatus === 'healthy' || b.normalizedStatus === 'warning'
            ).length}</span>
            <span className="stat-label">Currently Available</span>
          </div>
        </div>
        
        <div 
          className="map-container" 
          style={{ 
            position: 'relative',
            width: '100%',
            height: '600px',
            minHeight: '600px'
          }}
        >
          <div 
            ref={mapRef} 
            id="map" 
            className="map"
            key="google-map"
            style={{ 
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%', 
              height: '100%',
              zIndex: map ? 2 : 0
            }}
          />
          {!map && !error && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#f0f0f0',
              color: '#666',
              fontSize: '18px',
              zIndex: 1
            }}>
              Loading map...
            </div>
          )}
          {!map && error && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#f8d7da',
              color: '#721c24',
              fontSize: '16px',
              padding: '20px',
              textAlign: 'center',
              zIndex: 1
            }}>
              {error}
            </div>
          )}
        </div>
        
        {blocks.length === 0 && (
          <div className="no-blocks-message">
            <p>No Block dispensers found. Please check your data or contact support.</p>
          </div>
        )}
      </div>
      
      <Footer />
    </div>
  );
};

export default BlockMap; 